import { useEffect, useState } from "react";
import { Save, Folder, ExternalLink, X, Sparkles } from "lucide-react";
import { ContentPicker } from "../ContentPicker";
import { saveVizAsCognosReport, type SaveVizApiError, type SaveVizResult } from "@/lib/saveVizApi";
import {
  getAuthoringConfig,
  authorReport,
  promoteAuthored,
  type AuthoringConfig,
  type AuthorResult,
  type AuthoringApiError,
} from "@/lib/authoringApi";

interface Props {
  columnNames: string[];
  rows:        Array<Array<unknown>>;
  /**
   * Lazily capture the rendered chart as a PNG data URL. Invoked once
   * at submit time so the saved Cognos report embeds the visual above
   * the data table; resolves to null when nothing capturable is
   * present, in which case the save degrades to table-only.
   */
  captureImage: () => Promise<string | null>;
  /**
   * B0 (save-viz Feature B): effective source ref of the chart's data.
   * Forwarded to the save endpoint (allow-listed kinds only) so
   * model-backed sources can save as live native reports.
   */
  sourceRef?:  { kind: string; params: Record<string, unknown> } | null;
  /** The active chart's spec triplet, forwarded alongside sourceRef. */
  vizSpec?:    { format: string; vizType: string; specJson: string } | null;
  onClose:     () => void;
}

/**
 * Persist the current chart as a Cognos report. Two modes:
 *
 * <ul>
 *   <li><b>Direct save</b> (default) — writes straight to a folder you
 *       pick, via {@code /api/v1/visualize/save}.</li>
 *   <li><b>Stage for review</b> (when authoring is enabled) — writes to
 *       the configured authoring staging folder via the governed
 *       authoring workflow, then offers a "Promote…" step to move the
 *       reviewed report to its real home.</li>
 * </ul>
 */
export function SaveVizModal({ columnNames, rows, captureImage, sourceRef, vizSpec, onClose }: Props) {
  const [reportName,   setReportName]   = useState("");
  const [targetFolder, setTargetFolder] = useState("");
  const [folderLabel,  setFolderLabel]  = useState("");
  const [subtitle,     setSubtitle]     = useState("");
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [busy,         setBusy]         = useState(false);
  const [error,        setError]        = useState<SaveVizApiError | null>(null);
  const [result,       setResult]       = useState<SaveVizResult | null>(null);

  // Authoring (staging) mode.
  const [cfg,            setCfg]            = useState<AuthoringConfig | null>(null);
  const [staged,         setStaged]         = useState(false);
  const [stagedResult,   setStagedResult]   = useState<AuthorResult | null>(null);
  const [authoringError, setAuthoringError] = useState<AuthoringApiError | null>(null);
  const [promotePickerOpen, setPromotePickerOpen] = useState(false);
  const [promoting,      setPromoting]      = useState(false);
  const [promoteResult,  setPromoteResult]  = useState<AuthorResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAuthoringConfig().then((c) => { if (!cancelled) setCfg(c); });
    return () => { cancelled = true; };
  }, []);

  const authoringAvailable = !!cfg?.enabled && cfg.stagingConfigured;
  const done = result || stagedResult;
  const canSubmit =
    !busy && reportName.trim() !== "" && (staged || targetFolder.trim() !== "") && !done;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setAuthoringError(null);
    try {
      const chartImageDataUrl = await captureImage();
      if (staged) {
        const r = await authorReport({
          reportName: reportName.trim(),
          subtitle:   subtitle.trim() || undefined,
          columns:    columnNames,
          rows,
          chartImageDataUrl: chartImageDataUrl ?? undefined,
        });
        setStagedResult(r);
      } else {
        const r = await saveVizAsCognosReport(
          reportName.trim(), targetFolder.trim(), columnNames, rows,
          subtitle.trim() || undefined, chartImageDataUrl ?? undefined,
          sourceRef ?? undefined, vizSpec ?? undefined,
        );
        setResult(r);
      }
    } catch (e) {
      if (staged) {
        const a = e as AuthoringApiError;
        setAuthoringError({ code: a?.code ?? "unknown", detail: a?.detail ?? "Authoring failed.", status: a?.status ?? 0 });
      } else {
        const a = e as SaveVizApiError;
        setError({ code: a?.code ?? "unknown", detail: a?.detail ?? "Unknown error from save endpoint.", status: a?.status ?? 0 });
      }
    } finally {
      setBusy(false);
    }
  }

  async function doPromote(destFolder: string) {
    if (!stagedResult) return;
    const source = stagedResult.searchPath || stagedResult.storeId;
    if (!source) return;
    setPromoting(true);
    setAuthoringError(null);
    try {
      const r = await promoteAuthored(source, destFolder, undefined);
      setPromoteResult(r);
    } catch (e) {
      const a = e as AuthoringApiError;
      setAuthoringError({ code: a?.code ?? "unknown", detail: a?.detail ?? "Promote failed.", status: a?.status ?? 0 });
    } finally {
      setPromoting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-viz-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy && !promoting) onClose(); }}
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="save-viz-modal-title" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Save className="h-4 w-4" aria-hidden="true" />
            Save as Cognos report
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy || promoting}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {result ? (
          <section className="px-4 py-5 text-sm">
            <p className="font-medium text-emerald-700">Saved.</p>
            <p className="mt-2 text-slate-700">
              <span className="font-medium">{result.reportName}</span> is now in the Cognos content store.
            </p>
            {result.searchPath && (
              <p className="mt-1 break-all font-mono text-[11px] text-slate-500">{result.searchPath}</p>
            )}
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={onClose}
                className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900">
                Done
              </button>
            </div>
          </section>
        ) : stagedResult ? (
          <section className="px-4 py-5 text-sm">
            {promoteResult ? (
              <>
                <p className="font-medium text-emerald-700">Promoted.</p>
                <p className="mt-2 text-slate-700">
                  <span className="font-medium">{reportName}</span> is now live in the content store.
                </p>
                {promoteResult.searchPath && (
                  <p className="mt-1 break-all font-mono text-[11px] text-slate-500">{promoteResult.searchPath}</p>
                )}
              </>
            ) : (
              <>
                <p className="flex items-center gap-1.5 font-medium text-sky-700">
                  <Sparkles className="h-4 w-4" aria-hidden="true" /> Staged for review.
                </p>
                <p className="mt-2 text-slate-700">
                  <span className="font-medium">{reportName}</span> was created in the authoring staging folder.
                </p>
                {stagedResult.searchPath && (
                  <p className="mt-1 break-all font-mono text-[11px] text-slate-500">{stagedResult.searchPath}</p>
                )}
                <p className="mt-3 text-[12px] text-slate-500">
                  Review it in Cognos, then promote it to its real home — or leave it staged.
                </p>
                {authoringError && (
                  <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                    <p className="font-semibold">Promote failed ({authoringError.code}).</p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words">{authoringError.detail}</p>
                  </div>
                )}
              </>
            )}
            <div className="mt-4 flex justify-end gap-2">
              {!promoteResult && (
                <button type="button" onClick={() => setPromotePickerOpen(true)} disabled={promoting}
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50">
                  {promoting ? "Promoting…" : "Promote to a folder…"}
                </button>
              )}
              <button type="button" onClick={onClose}
                className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900">
                Done
              </button>
            </div>
          </section>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3 px-4 py-4 text-sm">
            <div>
              <label htmlFor="save-viz-name" className="block text-xs font-medium text-slate-700">Report name</label>
              <input id="save-viz-name" type="text" value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="e.g. Av Sales — Categories snapshot"
                disabled={busy} maxLength={200} required
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-50" />
            </div>

            {authoringAvailable && (
              <label className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50/50 px-3 py-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={staged} disabled={busy}
                  onChange={(e) => setStaged(e.target.checked)} className="mt-0.5" />
                <span>
                  <span className="font-medium text-sky-800">Stage for review</span> — create in the authoring
                  sandbox first, then promote it live. {cfg?.stagingFolder && (
                    <span className="break-all font-mono text-[10px] text-slate-500">{cfg.stagingFolder}</span>
                  )}
                </span>
              </label>
            )}

            {!staged && (
              <div>
                <label className="block text-xs font-medium text-slate-700">Target folder</label>
                <div className="mt-1 flex items-center gap-2">
                  <button type="button" onClick={() => setPickerOpen(true)} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    <Folder className="h-3.5 w-3.5" aria-hidden="true" />
                    {targetFolder ? "Change…" : "Pick folder…"}
                  </button>
                  {targetFolder && (
                    <span className="truncate text-[12px] text-slate-600" title={targetFolder}>
                      {folderLabel || targetFolder}
                    </span>
                  )}
                </div>
                {targetFolder && (
                  <p className="mt-1 break-all font-mono text-[10px] text-slate-400">{targetFolder}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="save-viz-subtitle" className="block text-xs font-medium text-slate-700">
                Subtitle <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input id="save-viz-subtitle" type="text" value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Short note shown under the report title"
                disabled={busy} maxLength={500}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-50" />
            </div>

            <p className="text-[11px] text-slate-500">
              {rows.length.toLocaleString()} row{rows.length === 1 ? "" : "s"},{" "}
              {columnNames.length} column{columnNames.length === 1 ? "" : "s"} will be saved as a static snapshot.
            </p>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                <p className="font-semibold">Save failed ({error.code}).</p>
                <p className="mt-0.5 whitespace-pre-wrap break-words">{error.detail}</p>
              </div>
            )}
            {authoringError && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                <p className="font-semibold">Authoring failed ({authoringError.code}).</p>
                <p className="mt-0.5 whitespace-pre-wrap break-words">{authoringError.detail}</p>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={busy}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
                Cancel
              </button>
              <button type="submit" disabled={!canSubmit}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50">
                {busy ? (
                  <>
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                    {staged ? "Staging…" : "Saving…"}
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    {staged ? "Stage for review" : "Save to Cognos"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {pickerOpen && (
          <ContentPicker
            open={pickerOpen}
            title="Pick a target folder for the saved report"
            initialPath={targetFolder || undefined}
            onCancel={() => setPickerOpen(false)}
            onConfirm={(picked) => {
              setTargetFolder(picked.path);
              setFolderLabel(picked.name);
              setPickerOpen(false);
            }}
          />
        )}
        {promotePickerOpen && (
          <ContentPicker
            open={promotePickerOpen}
            title="Pick the destination folder to promote into"
            onCancel={() => setPromotePickerOpen(false)}
            onConfirm={(picked) => {
              setPromotePickerOpen(false);
              void doPromote(picked.path);
            }}
          />
        )}
      </div>
    </div>
  );
}
